import React from "react";
import { Button, ProgressBar, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ShowToast } from "../common/CommonController";
import projectApp from "../Shared/Controller/projectApp";

// Static progress steps configuration - 0% to 60% gradually
const PROGRESS_STEPS = [
  { percent: 15, label: 'Analyzing file', duration: 1500 },
  { percent: 30, label: 'Analyzing file', duration: 1500 },
  { percent: 45, label: 'Validating Structure', duration: 1500 },
  { percent: 60, label: 'Validating Structure', duration: 1500 }
];

class ProjectColumn extends projectApp {

  constructor(props) {
    super(props)
    this.state = {
      fileLoading: false,
      saveFileLoading: false,
      file_columns: [],
      headerIds: [],
      reviewHeaderIds: [],
      fileMessage: "",
      importStatus: "Success",
      isEdit: false,
      progress: 0,
      nextCall: true,
      // Static progress state
      showStaticProgress: false,
      staticProgress: 0,
      staticProgressStep: '',
      staticProgressInterval: null,
    }
  }


  componentDidMount = () => {
    this.getFileColumn(this.props.project_id);
    localStorage.setItem("nextCall", true);
  }

  componentWillUnmount = () => {
    this.cleanupStaticProgress();
    localStorage.setItem("nextCall", false);
  }

  // Get current step label based on progress percentage
  getCurrentStepLabel = (progress, importStatus) => {
    if (progress >= 0 && progress < 30) {
      return 'Analyzing file';
    } else if (progress >= 30 && progress < 60) {
      return 'Validating Structure';
    } else if (progress >= 60 && progress < 80) {
      return 'Checking Data';
    } else if (progress >= 80 && progress < 100) {
      return 'Importing Data';
    } else if (progress >= 100 || importStatus === "Success") {
      return 'Completed';
    }
    return 'Analyzing file';
  }

  // Static progress animation methods
  startStaticProgress = () => {
    this.staticProgressStartTime = Date.now();
    // Start at first step percent
    const firstStep = PROGRESS_STEPS[0] || { percent: 15, label: 'Analyzing file', duration: 1500 };
    this.setState({ 
      showStaticProgress: true, 
      staticProgress: firstStep.percent, 
      staticProgressStep: firstStep.label
    });

    // We'll increment by 1% until we reach each step target.
    let currentStepIndex = 0; // index of the step whose label we're showing
    // next target index (we increment towards this target)
    let nextTargetIndex = 1;

    const tick = () => {
      const { staticProgress } = this.state;

      // If there is no next target, stop ticking
      if (nextTargetIndex >= PROGRESS_STEPS.length) {
        // final target reached — ensure last displayed percent is final step percent
        this.setState({ staticProgress: PROGRESS_STEPS[PROGRESS_STEPS.length - 1].percent });
        if (this._staticProgressInterval) {
          clearInterval(this._staticProgressInterval);
          this._staticProgressInterval = null;
          this._staticProgressIntervalIntervalMs = null;
        }
        return;
      }

      const prevPercent = PROGRESS_STEPS[nextTargetIndex - 1].percent;
      const targetPercent = PROGRESS_STEPS[nextTargetIndex].percent;
      const stepDuration = PROGRESS_STEPS[nextTargetIndex].duration || 1500;
      const increments = Math.max(1, targetPercent - prevPercent);
      // interval to move +1 percent such that total time ~= stepDuration
      const intervalMs = Math.max(20, Math.floor(stepDuration / increments));

      // If current progress is less than target, increment by 1
      if (staticProgress < targetPercent) {
        // use setState functional update to avoid stale state
        this.setState((prevState) => ({
          staticProgress: Math.min(prevState.staticProgress + 1, targetPercent)
        }));
      } else {
        // reached targetPercent -> advance to next step
        currentStepIndex = nextTargetIndex;
        this.setState({
          staticProgressStep: PROGRESS_STEPS[currentStepIndex].label
        });
        nextTargetIndex++;
      }

      // adjust interval if needed (clear and recreate with new interval ms when target changed)
      if (this._staticProgressInterval && this._staticProgressIntervalIntervalMs !== intervalMs) {
        clearInterval(this._staticProgressInterval);
        this._staticProgressIntervalIntervalMs = intervalMs;
        this._staticProgressInterval = setInterval(tick, intervalMs);
      }
    };

    // initial interval based on first transition (from first step to second)
    const initialIntervalMs = (() => {
      if (PROGRESS_STEPS.length < 2) return 100;
      const prev = PROGRESS_STEPS[0].percent;
      const target = PROGRESS_STEPS[1].percent;
      const dur = PROGRESS_STEPS[1].duration || 1500;
      const inc = Math.max(1, target - prev);
      return Math.max(20, Math.floor(dur / inc));
    })();

    // store native interval id (not in state) to avoid repeated setState
    this._staticProgressIntervalIntervalMs = initialIntervalMs;

    // Pause at the first step (15%) for 1250ms, then start smooth +1% ticking
    if (this._staticProgressDelayTimeout) {
      clearTimeout(this._staticProgressDelayTimeout);
      this._staticProgressDelayTimeout = null;
    }
    this._staticProgressDelayTimeout = setTimeout(() => {
      // start the ticking interval after the initial pause
      if (this._staticProgressInterval) {
        clearInterval(this._staticProgressInterval);
      }
      this._staticProgressInterval = setInterval(tick, initialIntervalMs);
      this._staticProgressDelayTimeout = null;
    }, 1250);
  }

  stopStaticProgress = (successOrFail) => {
    // Clear internal interval if present
    if (this._staticProgressInterval) {
      clearInterval(this._staticProgressInterval);
      this._staticProgressInterval = null;
      this._staticProgressIntervalIntervalMs = null;
    }
    // Clear the startup delay if still pending
    if (this._staticProgressDelayTimeout) {
      clearTimeout(this._staticProgressDelayTimeout);
      this._staticProgressDelayTimeout = null;
    }
    // Ensure final progress corresponds to last static step (60)
    const finalProgress = PROGRESS_STEPS.length ? PROGRESS_STEPS[PROGRESS_STEPS.length - 1].percent : 60;
    const finalStep = PROGRESS_STEPS.length ? PROGRESS_STEPS[PROGRESS_STEPS.length - 1].label : 'Validating Structure';

    // set final values and hide static progress so dynamic progress can take over
    this.setState({
      staticProgress: finalProgress,
      staticProgressStep: finalStep,
      staticProgressInterval: null,
      showStaticProgress: false
    });
  }

  cleanupStaticProgress = () => {
    if (this._staticProgressInterval) {
      clearInterval(this._staticProgressInterval);
      this._staticProgressInterval = null;
      this._staticProgressIntervalIntervalMs = null;
    }
    // Clear any pending startup delay
    if (this._staticProgressDelayTimeout) {
      clearTimeout(this._staticProgressDelayTimeout);
      this._staticProgressDelayTimeout = null;
    }
    // Also clear any state-stored interval reference for compatibility
    if (this.state.staticProgressInterval) {
      clearInterval(this.state.staticProgressInterval);
      this.setState({ staticProgressInterval: null });
    }
  }

  // Override getFileColumn to integrate static progress
  getFileColumn = (project_id) => {
    this.startStaticProgress();
    // Clear any stale error/status so Next button is not hidden after a past failure.
    this.setState({ fileLoading: true, fileMessage: "", importStatus: "Success", progress: 0 })
    let values = {
      projectid: project_id
    }
    
    const ProjectServices = require('../Shared/Services/ProjectServices').default;
    
    ProjectServices.getFileColumn(values).then((response) => {
      if (response.data.code === 1) {
        if (response.data.data) {
          // Wait for static progress to complete before showing data
          setTimeout(() => {
            this.setState({ file_columns: response.data.data, fileMessage: "" },
              () => {
                this.afterGetColumnSuccess(response.data.data);
                this.stopStaticProgress("success");
              })
          }, 6000 - (Date.now() - this.staticProgressStartTime || 0));
        }
        else {
          setTimeout(() => {
            this.setState({ file_columns: [] })
            this.stopStaticProgress("success");
          }, 6000 - (Date.now() - this.staticProgressStartTime || 0));
        }
      }
      else {
        if (response.data.message.constructor === Object) {
          // ShowToast("object", "error", response.data.message)
        }
        else {
          // Wait for static progress to complete (6 seconds) before showing error
          const timeElapsed = Date.now() - (this.staticProgressStartTime || Date.now());
          const remainingTime = Math.max(6000 - timeElapsed, 0);
          
          setTimeout(() => {
            // Set progress and status AFTER static progress completes
            const initialProgress = response.data.extra_details ? parseInt(response.data.extra_details.percentage) : 60;
            // preserve current progress (do not decrease). default to 60 if no prior progress
            const currentProgress = this.state.progress || this.state.staticProgress || 60;
            const targetProgress = initialProgress >= 60 ? initialProgress : 60;
            this.setState({ 
              fileMessage: response.data.message, 
              importStatus: response.data.import_status, 
              progress: Math.max(currentProgress, targetProgress)
            }, () => {
              // Stop static progress and start dynamic
              this.stopStaticProgress("fail");
              this.getFileProgress(this.props.project_id);
            });
          }, remainingTime);
        }
      }
      this.setState({ fileLoading: false });
    }).catch(error => {
      ShowToast("message", "error", "" + error);
      this.setState({ fileLoading: false });
      this.stopStaticProgress("fail");
    });
  }

  // Override GetFileProgress to show gradual progress increase
  GetFileProgress = (project_id) => {
    let values = {
      projectid: project_id
    }
    localStorage.setItem("nextCall", false);

    const ProjectServices = require('../Shared/Services/ProjectServices').default;

    ProjectServices.getFileProgress(values).then((response) => {
      if (response.data.code === 1) {
        if (response.data.import_status === "Success") {
          // Progress through 60-80% Checking Data, then 80-90% Importing Data, then 100% Completed
          const currentProgress = this.state.progress || 60;
          
          if (currentProgress < 80) {
            // Progress through Checking Data (60-80%)
            this.setState({
              progress: Math.min(currentProgress + 2, 80),
              importStatus: "Pending",
              fileMessage: "",
            }, () => {
              if (this.state.progress >= 80) {
                // Move to Importing Data after reaching 80%
                setTimeout(() => {
                  this.setState({
                    progress: 90,
                    importStatus: "Pending",
                  }, () => {
                    // After 1 second, show 100% with "Completed"
                    setTimeout(() => {
                      this.setState({
                        progress: 100,
                        importStatus: "Success",
                      }, () => {
                        // After 2 more seconds at 100%, load data and hide progress
                        setTimeout(() => {
                          this.setState({
                            file_columns: response.data.data,
                            progress: 0,
                          })
                          this.setDefaultFileColumns(response.data.data)
                        }, 2000);
                      });
                    }, 1000);
                  });
                }, 500);
              }
            });
          } else {
            // Already at 80%+, go to Importing Data then Completed
            this.setState({
              progress: 90,
              importStatus: "Pending",
            }, () => {
              setTimeout(() => {
                this.setState({
                  progress: 100,
                  importStatus: "Success",
                }, () => {
                  setTimeout(() => {
                    this.setState({
                      file_columns: response.data.data,
                      progress: 0,
                    })
                    this.setDefaultFileColumns(response.data.data)
                  }, 2000);
                });
              }, 1000);
            });
          }
        }
        else if (response.data.import_status === "Pending") {
          // Gradually increase progress, starting from 60% and capping at 80%
          let currentProgress = this.state.progress || 60;
          let newProgress = currentProgress;
          
          // If progress is less than 60, start at 60
          if (currentProgress < 60) {
            newProgress = 60;
          } 
          // Gradually increase by small increments, but cap at 80%
          else if (currentProgress < 80) {
            newProgress = Math.min(currentProgress + 2, 80);
          }
          
          this.setState({
            progress: newProgress,
            importStatus: response.data.import_status,
          })
        }
        else if (response.data.import_status === "Failed") {
          // Show random progress between 60-80% for failed status
          const randomProgress = Math.floor(Math.random() * (80 - 60 + 1)) + 60;
          // do not decrease visible progress — only increase if randomProgress is higher
          const currentProgress = this.state.progress || 60;
          
          this.setState({
            progress: Math.max(currentProgress, randomProgress),
            importStatus: response.data.import_status,
            fileMessage: response.data.message || "",
          })
        }
        localStorage.setItem("nextCall", true);
        this.getFileProgress();
      }
      else {
        if (response.data.message.constructor === Object) {
          ShowToast("object", "error", response.data.message)
        }
        else {
          ShowToast("message", "error", response.data.message);
          // preserve current progress on unexpected non-success responses (do not drop it)
          this.setState({ 
            fileMessage: response.data.message, 
            progress: this.state.progress || 60,
            importStatus: "Failed"
          });
        }
      }
    }).catch(error => {
      ShowToast("message", "error", "" + error);
    });
  }

  afterGetColumnSuccess = (data) => {
    if (this.props.currentStep === 3 && this.props.projectDetail.last_completed_step >= 2 && this.props.projectDetail.activeheaderids.length > 0) {
      this.setState({
        isEdit: true,
        headerIds: this.props.projectDetail.activeheaderids,
        reviewHeaderIds: this.props.projectDetail.activereviewheaderids
      });
    }
    else {
      this.setDefaultFileColumns(data);
    }
  }

  getFileProgress = () => {
    setTimeout(() => {
      let nextCall = localStorage.getItem("nextCall");
      if (this.state.importStatus === "Pending" && nextCall === "true") {
        this.GetFileProgress(this.props.project_id)
      }
    }, 3000);
  }

  setDefaultFileColumns = (data) => {
    let headerIds = [];
    let reviewHeaderIds = [];
    localStorage.setItem("nextCall", false);
    let lastThreeRecord = data.length - 3
    if (data && data.length > 0) {
      data.map((item, index) => {
        // if (item.column_heading === "Account_Reference" || item.column_heading === "Current_Balance" || item.column_heading === "Delinquency_Flag") {
        if (this.props.projectDetail.activeheaderids && this.props.projectDetail?.activeheaderids.length === 0) {
          if (this.isDefaultColumn(item.column_heading)) {
            headerIds.push(item.id);
          }
          else {
            if (index <= 2) {
              headerIds.push(item.id);
            }
            if (index >= lastThreeRecord) {
              headerIds.push(item.id);
            }
          }
        }
        /* Set Review HeaderIds */
        if (this.props.projectDetail.activereviewheaderids && this.props.projectDetail?.activereviewheaderids.length === 0) {
          reviewHeaderIds.push(item.id);
        }
        /* End Review HeaderIds */
        return null
        // }
      })
    }
    this.setState({ isEdit: true, headerIds: headerIds, reviewHeaderIds: reviewHeaderIds })
  }

  onHandleChange = (e) => {
    let headerids = [...this.state.headerIds];
    let columnindex = headerids.indexOf(parseInt(e.target.value));
    if (columnindex >= 0) {
      headerids.splice(columnindex, 1);
    }
    else {
      headerids.push(parseInt(e.target.value));
    }
    this.setState({
      headerIds: headerids
    })
  }

  onReviewHandleChange = (e) => {
    let reviewHeaderIds = [...this.state.reviewHeaderIds];
    let columnindex = reviewHeaderIds.indexOf(parseInt(e.target.value));
    if (columnindex >= 0) {
      reviewHeaderIds.splice(columnindex, 1);
    }
    else {
      reviewHeaderIds.push(parseInt(e.target.value));
    }
    this.setState({
      reviewHeaderIds: reviewHeaderIds
    });
  }

  renderIntegrityCheckboxItem = (citem) => {
    let value = "";
    if (this.props.projectDetail.activeheaderids && this.props.projectDetail?.activeheaderids.length > 0) {
      let element = this.props.projectDetail.activeheaderids && this.props.projectDetail.activeheaderids.find((item) => parseInt(citem.id) === item);
      if (this.isDefaultColumn(element ? citem.column_heading : "")) {
        value = <input type="checkbox" disabled="disabled"
          checked="checked"
          className="custom-chkbox"
          onClick={(e) => this.onHandleChange(e)}
          value={citem.id}
          name={citem.column_heading} />
      }
      else {
        value = <input type="checkbox"
          defaultChecked={citem.id === element ? "checked" : ""}
          className="custom-chkbox"
          onClick={(e) => this.onHandleChange(e)}
          value={citem.id}
          name={citem.column_heading} />
      }
    }
    else if (this.props.projectDetail.activeheaderids && this.props.projectDetail?.activeheaderids.length === 0) {
      if (this.isDefaultColumn(citem.column_heading)) {
        value = <input type="checkbox" disabled="disabled"
          checked="checked"
          className="custom-chkbox"
          onClick={(e) => this.onHandleChange(e)}
          value={citem.id}
          name={citem.column_heading} />
      }
      else {
        value = <input type="checkbox"
          defaultChecked={this.setSpacifiedColumn(citem.id)}
          // defaultChecked="checked"
          className="custom-chkbox"
          onClick={(e) => this.onHandleChange(e)}
          value={citem.id}
          name={citem.column_heading} />
      }
    }
    /* else {
      value = <input type="checkbox"
        defaultChecked="checked"
        onClick={(e) => this.onHandleChange(e)}
        value={citem.id}
        name={citem.column_heading} />
    } */
    return value;
  }

  renderReviewCheckboxItem = (citem) => {
    let value = "";
    if (this.props.projectDetail.activereviewheaderids && this.props.projectDetail?.activereviewheaderids.length > 0) {
      let element = this.props.projectDetail.activereviewheaderids.find((item) => parseInt(citem.id) === item);
      if (this.isDefaultColumn(element ? citem.column_heading : "")) {
        value = <input type="checkbox" disabled="disabled"
          checked="checked"
          className="custom-chkbox"
          onClick={(e) => this.onReviewHandleChange(e)}
          value={citem.id}
          name={citem.column_heading} />
      }
      else {
        value = <input type="checkbox"
          // defaultChecked={"checked"}
          defaultChecked={citem.id === element ? "checked" : ""}
          className="custom-chkbox"
          onClick={(e) => this.onReviewHandleChange(e)}
          value={citem.id}
          name={citem.column_heading} />
      }
    }
    else if (this.props.projectDetail.activereviewheaderids && this.props.projectDetail?.activereviewheaderids.length === 0) {
      if (this.isDefaultColumn(citem.column_heading)) {
        value = <input type="checkbox" disabled="disabled"
          checked="checked"
          className="custom-chkbox"
          onClick={(e) => this.onReviewHandleChange(e)}
          value={citem.id}
          name={citem.column_heading} />
      }
      else {
        value = <input type="checkbox"
          // defaultChecked={this.setSpacifiedColumn(citem.id)}
          defaultChecked="checked"
          className="custom-chkbox"
          onClick={(e) => this.onReviewHandleChange(e)}
          value={citem.id}
          name={citem.column_heading} />
      }
    }
    /* else {
      value = <input type="checkbox"
        defaultChecked="checked"
        onClick={(e) => this.onHandleChange(e)}
        value={citem.id}
        name={citem.column_heading} />
    } */
    return value;
  }

  isDefaultColumn = (item) => {
    let isDefault = false;
    if (item === "Account_Reference" || item === "Current_Balance" || item === "Delinquency_Flag") {
      isDefault = true;
    }
    return isDefault;
  }

  setSpacifiedColumn = (checkedid) => {
    let isFindChecked = false;
    let lastThreeRecord = this.state.file_columns.length - 3
    this.state.file_columns.map((item, index) => {
      if (index <= 2 && item.id === checkedid) {
        isFindChecked = true;
      }
      if (index >= lastThreeRecord && item.id === checkedid) {
        isFindChecked = true;
      }
    });
    return isFindChecked;
  }

  onSaveHeaderColumn = (e) => {
    e.preventDefault()
    let itemChecked = false;
    for (let findex = 0; findex < this.state.file_columns.length; findex++) {
      let fitem = this.state.file_columns[findex];
      if (!this.isDefaultColumn(fitem.column_heading)) {
        itemChecked = this.state.headerIds.find((hitem) => hitem === fitem.id);
        if (itemChecked) {
          break;
        }
      }
    }
    if (!itemChecked) {
      ShowToast("message", "error", "Please mark at least one integrity data column");
    }
    else {
      this.saveHeaderColumns();
    }
  }

  onColumnPreviousClick = (e) => {
    e.preventDefault();
    this.props.callBackPreviousColumn();
  }

  checkColumnsNoteMsg = () => {
    let msg = " ";
    if (this.props.projectDetail.only_review === 0 && this.props.projectDetail.only_integrity === 0) {
      msg = "Data Integrity and Data Review";
    }
    else if (this.props.projectDetail.only_review === 1 && this.props.projectDetail.only_integrity === 0) {
      msg = "Data Review";
    }
    else if (this.props.projectDetail.only_integrity === 1 && this.props.projectDetail.only_review === 0) {
      msg = "Data Integrity";
    }
    return msg;
  }

  render() {
    const hasFileColumns = this.state.file_columns && this.state.file_columns.length > 0;
    const isPendingProgress = this.state.importStatus === "Pending" && this.state.progress > 0;

    return (
      <React.Fragment>
        <div className="step-list-wrap">
          <h2 className="step-title">Data</h2>
          <div className="step-content">
            <form className="step-form">
              {this.state.fileLoading === false &&
                <>
                  {(() => {
                    console.log("----------------------------");
                    console.log("hasFileColumns: " + hasFileColumns);
                    console.log("isPendingProgress: " + isPendingProgress);
                    console.log("showStaticProgress: " + this.state.showStaticProgress);
                    console.log("fileMessage: " + this.state.fileMessage);
                    console.log("saveFileLoading: " + this.state.saveFileLoading);
                    console.log("importStatus: " + this.state.importStatus);
                    console.log("progress: " + this.state.progress);
                    return null;
                  })()}
                  {hasFileColumns && !this.state.showStaticProgress && !isPendingProgress && (
                    <div className="uplod-inner">
                      <b>
                        <p>The following column data was loaded from the source file</p>
                        <p>Please use the checkbox to include the column within a {this.checkColumnsNoteMsg()} Workflow</p>
                      </b>
                    </div>
                  )}
                  {!hasFileColumns && this.state.fileMessage !== "" && !this.state.showStaticProgress && !isPendingProgress && (
                    <div className="uplod-inner">
                      <b>
                        <p>{this.state.fileMessage}</p>
                      </b>
                    </div>
                  )}
                  {this.state.showStaticProgress && (
                    <div className="uplod-inner mt-3">
                      <div style={{ position: 'relative' }}>
                        <ProgressBar 
                          animated
                          variant="success"
                          now={this.state.staticProgress}
                        />
                        <div style={{ 
                          position: 'absolute', 
                          top: '50%', 
                          left: '50%', 
                          transform: 'translate(-50%, -50%)',
                          fontWeight: 'bold',
                          color: '#000',
                          zIndex: 1
                        }}>
                          {this.state.staticProgress}%
                        </div>
                      </div>
                      <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center' }}>
                        <Spinner animation="border" size="sm" style={{ marginRight: '8px', width: '16px', height: '16px' }} />
                        <span>{this.state.staticProgressStep}</span>
                      </div>
                    </div>
                  )}
                  {!this.state.showStaticProgress && (this.state.importStatus === "Pending" || this.state.importStatus === "Success") && this.state.progress > 0 && (
                    <div className="uplod-inner mt-3">
                      <div style={{ position: 'relative' }}>
                        <ProgressBar 
                          animated={this.state.importStatus === "Pending"}
                          variant="success"
                          now={this.state.progress}
                        />
                        <div style={{ 
                          position: 'absolute', 
                          top: '50%', 
                          left: '50%', 
                          transform: 'translate(-50%, -50%)',
                          fontWeight: 'bold',
                           color: '#000',
                          zIndex: 1
                        }}>
                          {this.state.progress}%
                        </div>
                      </div>
                      <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center' }}>
                        {this.state.importStatus === "Pending" && this.state.progress < 100 && (
                          <Spinner animation="border" size="sm" style={{ marginRight: '5px', width: '16px', height: '16px' }} />
                        )}
                        {this.state.importStatus === "Success" && this.state.progress === 100 && (
                          <span style={{ marginRight: '5px', color: 'green', fontSize: '16px', fontWeight: 700 }}>✓</span>
                        )}
                        <span>{this.getCurrentStepLabel(this.state.progress, this.state.importStatus)}</span>
                      </div>
                    </div>
                  )}
                  {!this.state.showStaticProgress && this.state.importStatus === "Failed" && this.state.progress > 0 && (
                    <div className="uplod-inner mt-3">
                      <div style={{ position: 'relative' }}>
                        <ProgressBar 
                          now={this.state.progress}
                          style={{ backgroundColor: '#f0f0f0' }}
                        >
                          <div style={{ 
                            width: this.state.progress + '%', 
                            backgroundColor: '#ee7608',
                            transition: 'width 0.6s ease'
                          }} className="progress-bar"></div>
                        </ProgressBar>
                        <div style={{ 
                          position: 'absolute', 
                          top: '50%', 
                          left: '50%', 
                          transform: 'translate(-50%, -50%)',
                          fontWeight: 'bold',
                          color: '#000',
                          zIndex: 1
                        }}>
                          {this.state.progress}%
                        </div>
                      </div>
                      <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '5px', color: 'red', fontSize: '16px', fontWeight: 700 }}>X</span>
                        <span>{this.getCurrentStepLabel(this.state.progress, this.state.importStatus)}</span>
                      </div>
                      {/* <div style={{ marginTop: '10px', padding: '10px', display: 'flex', alignItems: 'center' }}>
                        <span style={{ color: '#721c24' }}>Please check the logs.</span>
                      </div> */}
                    </div>
                  )}
                  {hasFileColumns &&
                    <table className="col-md-12 mt-3">
                      <thead>
                        <tr>
                          <th>Data Columns</th>
                          {this.props.projectDetail.only_review === 0 &&
                            <th className="text-center">Integrity</th>
                          }
                          {this.props.projectDetail.only_integrity === 0 &&
                            <th className="text-center">Remove Columns from Review</th>
                          }
                        </tr>
                      </thead>
                      <tbody>
                        {this.state.file_columns.map((citem) => {
                          return (
                            <React.Fragment key={citem.column_heading}>
                              <tr>
                                <td>
                                  <div>
                                    <label>
                                      <span>{citem.column_heading}</span>
                                    </label>
                                  </div>
                                </td>
                                {this.props.projectDetail.only_review === 0 &&
                                  <td className="text-center">
                                    <label>
                                      {this.renderIntegrityCheckboxItem(citem)}
                                      <span></span>
                                    </label>
                                  </td>
                                }
                                {this.props.projectDetail.only_integrity === 0 &&
                                  <td className="text-center">
                                    <label>
                                      {this.renderReviewCheckboxItem(citem)}
                                      <span></span>
                                    </label>
                                  </td>
                                }
                              </tr>
                            </React.Fragment>
                          )
                        })
                        }
                      </tbody>
                    </table>
                  }
                  {!hasFileColumns && this.state.fileMessage !== "" &&
                    <div className="setp-btn-row">
                      <Link className="btn btn-blue btn-nex" to="/projects">Close</Link>
                    </div>
                  }
                  {hasFileColumns &&
                    <div className="setp-btn-row">
                      <Button variant="secondary"
                        onClick={(e) => this.onColumnPreviousClick(e)}
                        className="btn btn-blue btn-pre">Previous</Button>
                      {this.state.saveFileLoading === false &&
                        <Button variant="secondary"
                          disabled={!hasFileColumns}
                          onClick={(e) => this.onSaveHeaderColumn(e)}
                          className="btn btn-blue btn-nex">
                          Next
                        </Button>
                      }
                      {this.state.saveFileLoading === true &&
                        <Button variant="secondary"
                          className="btn btn-blue btn-nex">
                          <Spinner animation="border" size="sm" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </Spinner>
                        </Button>
                      }
                    </div>
                  }
                </>
              }
              {this.state.fileLoading === true &&
                <div className="text-center">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              }
            </form>
          </div>
        </div>
      </React.Fragment>
    )
  }
}
export default ProjectColumn;